import React, { useState, useEffect, useRef } from 'react'
import { Input } from 'semantic-ui-react'
import debounce from 'lodash.debounce'

import { preconUserId } from 'environment'

import SaveService from 'services/save.service'
import DeckService from 'services/deck.service'

import { FormatType } from 'types/deck'
import { DeckListResponse } from 'services/apiTypes/deck.types'
import { DeckCardInfoType } from 'types/search'

import SimpleSpinner from 'components/elements/SimpleSpinner'
import DeckLink from 'components/misc/DeckLink'

import styles from './preconSelector.module.scss'

type Props = {
  format: FormatType
  onClose: () => void
}

const PreconSelector = ({ format, onClose }: Props) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [response, setResponse] = useState<DeckListResponse>()

  const [currentPage, setCurrentPage] = useState(1)

  const [fetchingDeck, setFetchingDeck] = useState(false)
  const [fetchingDeckError, setFetchingDeckError] = useState(null)

  const handleFetch = (filter = '') => {
    if (preconUserId === null) return setLoading(false)

    setLoading(true)
    setCurrentPage(1)

    DeckService.listV3({ deckFormat: format, ownerId: preconUserId, name: filter })
      .then(setResponse)
      .catch(setError)
      .finally(() => {
        setLoading(false)
      })
  }

  const loadNext = () => {
    if (!response?.next || preconUserId === null) return

    setLoading(true)

    // response.next doesn't work in the browser due to the API returning http rather than https. We should fix that
    // and use next instead here
    DeckService.listV3({}, response.next)
      .then(res => setResponse({ ...res, results: [...response.results, ...res.results] }))
      .catch(setError)
      .finally(() => setLoading(false))

    setCurrentPage(currentPage + 1)
  }

  const handleAddDeckToState = async (deck: DeckCardInfoType) => {
    setFetchingDeck(true)

    try {
      const precon = await DeckService.details(deck.id)

      // Stripping the existing deckRelationIds from the precons so that the SaveService sees them as new cards when they're added to the deck
      const cardsWithDeckRelationIdRemoved = precon.cards.map(c => ({ ...c, id: '' }))

      return SaveService.overwriteDeck({ ...precon, cards: cardsWithDeckRelationIdRemoved }).then(() => onClose())
    } catch (err: any) {
      setFetchingDeckError(err)
    } finally {
      setFetchingDeck(false)
    }
  }

  const debouncedSeach = useRef(debounce(handleFetch, 500)).current

  useEffect(handleFetch, [format])

  if (error) return <div>{error}</div>
  if (preconUserId === null) return <div>Woah, not sure what happened. But you should not be here</div>
  if (fetchingDeckError)
    return (
      <div>
        Uh oh, something happened while saving. Please try again. If this problem continues, please contact support
      </div>
    )

  if (fetchingDeck)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '1rem',
          padding: '1rem',
        }}>
        <div>Fetching and Saving your deck, this may take a moment</div>
        <SimpleSpinner />
      </div>
    )

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: '.5rem' }}>Select a Precon</h2>
        <span style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
          <Input
            loading={loading}
            placeholder="Search"
            icon="search"
            onChange={(_e, { value }) => debouncedSeach(value)}
          />
        </span>
      </div>
      <div className={styles.preconGrid}>
        {response?.results.map((deck, index) => (
          <button key={index} onClick={() => handleAddDeckToState(deck)}>
            <DeckLink deck={deck} displayOnly />
          </button>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          overflow: 'hidden',
          flexDirection: 'column',
        }}>
        {response?.next && (
          <button className={styles.plainButton} onClick={loadNext} disabled={loading} style={{ marginBottom: '2rem' }}>
            Load More
          </button>
        )}
        {loading && <SimpleSpinner />}
      </div>
    </>
  )
}

export default PreconSelector
